/**
 * useAutoWalk — fire-once auto-walk trigger for a screen's first visit.
 *
 * Usage:
 *   useAutoWalk(["step0_event_picker", "step0_vibe", "step0_ask_maya"]);
 *
 * The hook checks the seen-state on mount. If at least one beacon in
 * the sequence hasn't been seen yet AND no other beacon is currently
 * open, it starts the auto-walk after a small delay so the screen's
 * own entrance animations settle first.
 *
 * Returns nothing — pure side-effect.
 *
 * Why a separate hook? Each step component used to inline the same
 * useEffect; centralising means we get consistent timing, consistent
 * delay, consistent seen-check, and a single place to tune all of it.
 */
import { useEffect } from "react";

import useTour from "./useTour";

export default function useAutoWalk(sequence, { delayMs = 900, enabled = true } = {}) {
    const { startAutoWalk, hasBeenSeen, hasAutoWalked, openBeaconId } = useTour();

    useEffect(() => {
        if (!enabled || !Array.isArray(sequence) || sequence.length === 0) return undefined;
        // Jun 28 2026 — auto-open the tour at most ONCE per customer. After
        // the first screen has auto-walked, later screens never auto-pop —
        // the beacons stay available on tap (Tour / "?" pills) but don't
        // interrupt the glide. Keeps onboarding without nagging.
        if (hasAutoWalked?.()) return undefined;
        // Don't auto-walk if every beacon in the sequence has already
        // been seen — the customer is a returning user.
        if (sequence.every(hasBeenSeen)) return undefined;
        // Don't interrupt if another tour bubble is already open (e.g.
        // a previous screen's auto-walk hasn't finished yet).
        if (openBeaconId) return undefined;
        const t = setTimeout(() => {
            // Re-check inside the timeout in case the customer engaged
            // a beacon manually before the delay elapsed.
            if (!openBeaconId) startAutoWalk(sequence);
        }, delayMs);
        return () => clearTimeout(t);
        // We intentionally only depend on the sequence identity and the
        // `enabled` flag; we want this to fire on mount, not on every
        // openBeaconId change (which would re-arm the timer mid-walk).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, sequence.join("|")]);
}
